import debounce from 'lodash/debounce';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useConfig } from '@common/Hooks/useConfig';
import { useFetch } from '@common/Http';
import deparamUrl from '@helpers/deparamUrl';
import { API_SEARCH_SUGGESTIONS } from '@routes';
import { addSearchResults, addSearchTerm } from '../actions';

const parseResult = (result) => {
    const { id, path } = result;

    return {
        ...result,
        id: `${id}${path ? `_${path}` : ''}`,
        navigation_id: id
    };
};

const parseGroups = (accumulator, [key, value]) => ({
    ...accumulator,
    [key]: key === 'groups' ? value.map(parseResult) : value
});

const parseResponse = (response) => {
    const parseFunction = parseGroups;

    return Object.entries(response).reduce(parseFunction, {});
};

const getInitialSearchTerm = ({ serverSide, location }) => {
    const url = serverSide ? (location.split('?')[1] || '') : window.location.search;
    const searchKey = deparamUrl(url)?.search_key;

    const regex = /[([\]{}"'<>&)]/g;
    const searchKeyDelimiters = searchKey && searchKey.match(regex);

    if (searchKeyDelimiters && searchKeyDelimiters.length >= 2) {
        return '';
    }

    return searchKey;
};

export default (
    hasToDelayInitialSearchTermRender = false // used to initialize searchTerm on client side only to be able to cache component
) => {
    const dispatch = useDispatch();
    const config = useConfig();
    const { get } = useFetch();
    const initialSearchTerm = getInitialSearchTerm(config);
    const [
        searchTerm,
        setSearchTerm
    ] = useState(hasToDelayInitialSearchTermRender ? '' : initialSearchTerm);
    const [searchResults, setSearchResults] = useState({});
    const [fetchedSearchResults, setFetchedSearchResults] = useState();
    const hasSearchTerm = searchTerm?.trim()?.length > 1;

    const fetchSearchResults = useRef(debounce(
        async (value) => {
            const apiUrl = API_SEARCH_SUGGESTIONS;
            const gender = App.Config.navGender;
            const apiParameters = { search_key: value, gender };

            try {
                const response = await get(apiUrl, apiParameters);

                setFetchedSearchResults(parseResponse(response, config));
            } catch (error) {}
        },
        200,
        { leading: false, trailing: true }
    )).current;

    useEffect(() => {
        if (hasSearchTerm) {
            setSearchResults(fetchedSearchResults);
            dispatch(addSearchResults(fetchedSearchResults));
        }
    }, [dispatch, fetchedSearchResults, hasSearchTerm]);

    useEffect(() => {
        if (!hasSearchTerm) {
            setFetchedSearchResults({});
        }
    }, [hasSearchTerm]);

    useEffect(() => {
        if (hasSearchTerm) {
            fetchSearchResults(searchTerm);
            dispatch(addSearchTerm(searchTerm));
        } else {
            setSearchResults({});
            dispatch(addSearchResults({}));
        }
    }, [dispatch, fetchSearchResults, hasSearchTerm, searchTerm]);

    useEffect(() => {
        if (hasToDelayInitialSearchTermRender && initialSearchTerm) {
            setSearchTerm(initialSearchTerm);
        }
    }, [dispatch, hasToDelayInitialSearchTermRender, initialSearchTerm]);

    return [searchTerm, setSearchTerm, searchResults];
};
